// src/services/tokenService.js
import api from './api';

/**
 * Servicio centralizado para generar tokens temporales con scopes unificados
 * Mantiene retrocompatibilidad con la implementación existente
 */

/**
 * Configuración de tipos de token y sus scopes esperados
 */
const TOKEN_TYPE_CONFIG = {
  // Token para que el cliente complete su flujo de onboarding
  'onboarding': {
    description: 'Carga de documentos durante proceso de onboarding/compra',
    requiredScope: ['document_upload'],
    requiredFields: ['customer_id', 'purchase_request_id'],
    defaultExpiry: 30,
    singleUse: true
  },
  
  // Token para que el cliente autogestione todo su proceso
  'customer_self_service': {
    description: 'Cliente autogestiona su proceso de onboarding completo',
    requiredScope: ['customer_flow', 'document_upload', 'identity_validation', 'contract_signing'],
    requiredFields: ['customer_id', 'purchase_request_id'],
    defaultExpiry: 60,
    singleUse: false
  },
  
  // Token para acceder a documentos de una venta específica
  'sale': {
    description: 'Acceso a documentos de una venta específica',
    requiredScope: ['document_upload', 'document_view'],
    requiredFields: ['sale_id', 'customer_id'],
    defaultExpiry: 30,
    singleUse: false
  },
  
  // Token para que el cliente acceda a documentos de SU venta
  'customer': {
    description: 'Cliente accede a documentos de su propia venta',
    requiredScope: ['document_upload', 'document_view'],
    requiredFields: ['sale_id', 'customer_id'],
    defaultExpiry: 15,
    singleUse: false
  },
  
  // Token para vendedor acceda a todas las ventas de su sucursal
  'branch': {
    description: 'Vendedor de sucursal gestiona documentos de múltiples ventas',
    requiredScope: ['document_upload', 'document_view', 'sales_management'],
    requiredFields: [], // branch_id se infiere del usuario autenticado
    defaultExpiry: 120,
    singleUse: false
  }
};

/**
 * Clase para manejar tokens temporales de forma unificada
 */
export class TokenService {
  
  /**
   * Genera un token temporal con scope unificado
   * @param {Object} config - Configuración del token
   * @param {string} config.type - Tipo de token (onboarding, sale, customer, branch, customer_self_service)
   * @param {number} [config.expires_in] - Tiempo de expiración en minutos
   * @param {Object} config.context - Contexto específico del token
   * @returns {Promise<Object>} Respuesta con token y datos
   */
  static async generateToken({ type, expires_in, context = {} }) {
    const tokenConfig = TOKEN_TYPE_CONFIG[type];
    
    if (!tokenConfig) {
      throw new Error(`Tipo de token no soportado: ${type}`);
    }
    
    // Validar que se proporcionen los campos requeridos
    const missingFields = tokenConfig.requiredFields.filter(field => 
      !context[field] && context[field] !== 0
    );
    
    if (missingFields.length > 0) {
      throw new Error(`Campos requeridos faltantes para token tipo '${type}': ${missingFields.join(', ')}`);
    }
    
    // Preparar el payload para el API
    const payload = {
      type,
      scope: tokenConfig.requiredScope,
      expires_in: expires_in || tokenConfig.defaultExpiry,
      single_use: tokenConfig.singleUse,
      ...context
    };
    
    try {
      const response = await api.post('/temporary-token/generate', payload);
      return response.data;
    } catch (error) {
      console.error(`Error generando token tipo '${type}':`, error);
      throw error;
    }
  }
  
  /**
   * Genera token para carga de documentos durante onboarding
   * @param {string} customerId - ID del cliente
   * @param {string} purchaseRequestId - ID de la solicitud de compra
   * @param {number} [expiresIn=30] - Tiempo de expiración en minutos
   * @returns {Promise<Object>} Respuesta con token
   */
  static async generateOnboardingToken(customerId, purchaseRequestId, expiresIn = 30) {
    return this.generateToken({
      type: 'onboarding',
      expires_in: expiresIn,
      context: {
        customer_id: customerId,
        purchase_request_id: purchaseRequestId
      }
    });
  }
  
  /**
   * Genera token para autogestión completa del cliente
   * @param {string} customerId - ID del cliente
   * @param {string} purchaseRequestId - ID de la solicitud de compra
   * @param {number} [expiresIn=60] - Tiempo de expiración en minutos
   * @returns {Promise<Object>} Respuesta con token
   */
  static async generateCustomerSelfServiceToken(customerId, purchaseRequestId, expiresIn = 60) {
    return this.generateToken({
      type: 'customer_self_service',
      expires_in: expiresIn,
      context: {
        customer_id: customerId,
        purchase_request_id: purchaseRequestId
      }
    });
  }
  
  /**
   * Genera token para acceso a documentos de una venta específica
   * @param {string} saleId - ID de la venta
   * @param {string} customerId - ID del cliente
   * @param {number} [expiresIn=30] - Tiempo de expiración en minutos
   * @returns {Promise<Object>} Respuesta con token
   */
  static async generateSaleToken(saleId, customerId, expiresIn = 30) {
    return this.generateToken({
      type: 'sale',
      expires_in: expiresIn,
      context: {
        sale_id: saleId,
        customer_id: customerId
      }
    });
  }
  
  /**
   * Genera token para que el cliente acceda a documentos de SU venta
   * @param {string} saleId - ID de la venta
   * @param {string} customerId - ID del cliente
   * @param {number} [expiresIn=15] - Tiempo de expiración en minutos
   * @returns {Promise<Object>} Respuesta con token
   */
  static async generateCustomerToken(saleId, customerId, expiresIn = 15) {
    return this.generateToken({
      type: 'customer',
      expires_in: expiresIn,
      context: {
        sale_id: saleId,
        customer_id: customerId
      }
    });
  }
  
  /**
   * Genera token para acceso de sucursal a múltiples ventas
   * @param {number} [expiresIn=120] - Tiempo de expiración en minutos
   * @returns {Promise<Object>} Respuesta con token
   */
  static async generateBranchToken(expiresIn = 120) {
    return this.generateToken({
      type: 'branch',
      expires_in: expiresIn,
      context: {} // branch_id se infiere automáticamente del usuario autenticado
    });
  }
  
  /**
   * Valida un token temporal
   * @param {string} token - Token a validar
   * @returns {Promise<Object>} Respuesta con datos del token
   */
  static async validateToken(token) {
    try {
      const response = await api.post('/temporary-token/validate', { token });
      return response.data;
    } catch (error) {
      console.error('Error validando token:', error);
      throw error;
    }
  }
  
  /**
   * Genera URL móvil para un token
   * @param {string} token - Token generado
   * @param {string} type - Tipo de token
   * @returns {string} URL móvil
   */
  static generateMobileUrl(token, type) {
    const baseUrl = window.location.origin;
    
    // Determinar la ruta base según el tipo de token
    switch (type) {
      case 'onboarding':
      case 'customer_self_service':
        return `${baseUrl}/m/onboarding/${token}`;
        
      case 'sale':
      case 'customer':
      case 'branch':
        return `${baseUrl}/m/docs/${token}`;
        
      default:
        throw new Error(`Tipo de token no soportado para URL móvil: ${type}`);
    }
  }
  
  /**
   * Obtiene la configuración de un tipo de token
   * @param {string} type - Tipo de token
   * @returns {Object|null} Configuración del token
   */
  static getTokenConfig(type) {
    return TOKEN_TYPE_CONFIG[type] || null;
  }
  
  /**
   * Lista todos los tipos de token disponibles
   * @returns {Array<string>} Lista de tipos de token
   */
  static getAvailableTokenTypes() {
    return Object.keys(TOKEN_TYPE_CONFIG);
  }
}

// Exportar también las funciones individuales para mantener compatibilidad
export const {
  generateOnboardingToken,
  generateCustomerSelfServiceToken,
  generateSaleToken,
  generateCustomerToken,
  generateBranchToken,
  validateToken,
  generateMobileUrl,
  getTokenConfig,
  getAvailableTokenTypes
} = TokenService;

export default TokenService;
